import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, Boxes, Calculator, Eye, EyeOff, Image as ImageIcon, Loader2 } from "lucide-react";
import { Sparkles } from "lucide-react";
import PageHelp from "@/components/PageHelp";
import ServicesTab from "@/components/ServicesTab";

const Produits = () => {
  const { token } = useAuth();
  const [produits, setProduits] = useState([]);
  const [coutsDetail, setCoutsDetail] = useState([]);
  const [categories, setCategories] = useState(["Chaises", "Tables", "Accessoires", "Décoration", "Autre"]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    modele: "",
    couleur: "",
    categorie: "Chaises",
    cost_unit: "",
    prix_b2c_ttc: "",
    prix_b2b_ht: "",
    stock_initial: 0
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [produitsRes, coutsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/produits`, { headers }),
        axios.get(`${API}/couts-detail`, { headers }),
        axios.get(`${API}/categories`, { headers })
      ]);
      setProduits(produitsRes.data);
      setCoutsDetail(coutsRes.data);
      if (categoriesRes.data && categoriesRes.data.length > 0) {
        setCategories(categoriesRes.data);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const generateSKU = (modele, couleur) => {
    // Générer un SKU basé sur le modèle et la couleur
    const modeleCode = modele.substring(0, 3).toUpperCase();
    const couleurCode = couleur.substring(0, 3).toUpperCase();
    return `${modeleCode}-${couleurCode}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.modele || !formData.couleur) {
      toast.error("Veuillez remplir le modèle et la couleur");
      return;
    }

    try {
      const sku = generateSKU(formData.modele, formData.couleur);
      await axios.post(`${API}/produits`, {
        ...formData,
        modele: formData.modele.toUpperCase(),
        sku,
        cost_unit: parseFloat(formData.cost_unit),
        prix_b2c_ttc: parseFloat(formData.prix_b2c_ttc),
        prix_b2b_ht: parseFloat(formData.prix_b2b_ht),
        stock_initial: parseInt(formData.stock_initial)
      }, { headers });
      toast.success("Produit ajouté !");
      setDialogOpen(false);
      setFormData({
        modele: "",
        couleur: "",
        categorie: "Chaises",
        cost_unit: "",
        prix_b2c_ttc: "",
        prix_b2b_ht: "",
        stock_initial: 0
      });
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try {
      await axios.delete(`${API}/produits/${id}`, { headers });
      toast.success("Produit supprimé");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const [uploadingId, setUploadingId] = useState(null);

  const handleToggleVisible = async (produit) => {
    try {
      await axios.put(`${API}/produits/${produit.id}`, { visible_precommande: !produit.visible_precommande }, { headers });
      toast.success(!produit.visible_precommande ? "Produit visible en précommande" : "Produit masqué");
      fetchData();
    } catch { toast.error("Erreur"); }
  };

  const handleUploadImage = async (produit, file) => {
    if (!file) return;
    setUploadingId(produit.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await axios.post(`${API}/upload`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      const fileId = up.data.id;
      const url = `${API}/public/product-image/${fileId}`;
      await axios.put(`${API}/produits/${produit.id}`, { image_url: url }, { headers });
      toast.success("Photo mise à jour ✓");
      fetchData();
    } catch { toast.error("Erreur upload"); }
    finally { setUploadingId(null); }
  };

  const handleUpdateDescription = async (produit, description) => {
    try {
      await axios.put(`${API}/produits/${produit.id}`, { description }, { headers });
      fetchData();
    } catch { toast.error("Erreur"); }
  };

  const handleUploadAdditionalImage = async (produit, file) => {
    if (!file) return;
    const current = produit.images || [];
    if (current.length >= 5) { toast.error("Maximum 5 images en situation"); return; }
    setUploadingId(produit.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await axios.post(`${API}/upload`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      const url = `${API}/public/product-image/${up.data.id}`;
      const newImages = [...current, url];
      await axios.put(`${API}/produits/${produit.id}`, { images: newImages }, { headers });
      toast.success("Photo ajoutée ✓");
      fetchData();
    } catch { toast.error("Erreur upload"); }
    finally { setUploadingId(null); }
  };

  const handleRemoveAdditionalImage = async (produit, idx) => {
    const current = produit.images || [];
    const newImages = current.filter((_, i) => i !== idx);
    try {
      await axios.put(`${API}/produits/${produit.id}`, { images: newImages }, { headers });
      toast.success("Photo supprimée");
      fetchData();
    } catch { toast.error("Erreur"); }
  };

  const handleTogglePrecommande = async (produit) => {
    try {
      await axios.put(`${API}/produits/${produit.id}`, { en_precommande: !produit.en_precommande }, { headers });
      fetchData();
    } catch { toast.error("Erreur"); }
  };

  const handleUpdateDateArrivee = async (produit, date) => {
    try {
      await axios.put(`${API}/produits/${produit.id}`, { date_arrivee_estimee: date }, { headers });
      fetchData();
    } catch { toast.error("Erreur"); }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Get unique models from existing products for suggestions
  const existingModels = [...new Set(produits.map(p => p.modele))];
  const existingColors = [...new Set(produits.map(p => p.couleur))];
  
  // Group products by category
  const productsByCategory = produits.reduce((acc, p) => {
    const cat = p.categorie || "Chaises";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-fade-in" data-testid="produits-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-slate-800">Produits</h1>
            <PageHelp title="Produits" emoji="📦">
              <p>
                Cette page a <b>trois onglets</b>&nbsp;:
              </p>
              <ul className="space-y-1.5 list-disc pl-5">
                <li>
                  <b>Catalogue</b> — Liste de tes chaises (SKU, couleur,
                  prix de vente, stock). C'est ce qui est utilisé partout
                  dans l'app (ventes, livraisons, tournées).
                </li>
                <li>
                  <b>Détails des coûts</b> — Prix de revient par modèle
                  (prix usine, transport, taxes, stockage…).
                  <br />
                  <span className="text-slate-500">Utile pour calculer ta marge.</span>
                </li>
                <li>
                  <b>Services</b> 🆕 — Prestations non-physiques (assurance casse,
                  livraison, montage…). <b>Pas de stock</b> mais comptabilisées
                  dans les ventes avec leur propre CA.
                </li>
              </ul>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                ⚠️ <b>Important&nbsp;:</b> les deux onglets ne sont pas liés
                automatiquement. Les valeurs des "Détails des coûts" sont
                saisies <b>manuellement</b>. Si un chiffre te paraît faux
                (ex. Milan), modifie ou supprime simplement la ligne.
              </div>
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-xs text-indigo-800">
                💡 <b>Conseil&nbsp;:</b> commence par créer tes produits
                dans <b>Catalogue</b> (bouton "Nouveau produit"). Ensuite,
                va dans <b>Détails des coûts</b> pour noter le prix de
                revient de chaque modèle.
              </div>
            </PageHelp>
          </div>
          <p className="text-slate-500 mt-1">Catalogue et détail des coûts</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-produit-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau produit
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="produit-dialog">
            <DialogHeader>
              <DialogTitle>Ajouter un produit</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Catégorie *</Label>
                <Select
                  value={formData.categorie}
                  onValueChange={(value) => setFormData({ ...formData, categorie: value })}
                >
                  <SelectTrigger data-testid="produit-categorie-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Modèle *</Label>
                  <Input
                    type="text"
                    value={formData.modele}
                    onChange={(e) => setFormData({ ...formData, modele: e.target.value })}
                    placeholder="Ex: Milan, Ghost, Table Ronde..."
                    required
                    list="modele-suggestions"
                    data-testid="produit-modele-input"
                  />
                  <datalist id="modele-suggestions">
                    {existingModels.map((m) => (
                      <option key={m} value={m} />
                    ))}
                  </datalist>
                </div>

                <div className="space-y-2">
                  <Label>Couleur / Variante *</Label>
                  <Input
                    type="text"
                    value={formData.couleur}
                    onChange={(e) => setFormData({ ...formData, couleur: e.target.value })}
                    placeholder="Ex: Noir, Blanc, 120cm..."
                    required
                    list="couleur-suggestions"
                    data-testid="produit-couleur-input"
                  />
                  <datalist id="couleur-suggestions">
                    {existingColors.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Coût unitaire (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.cost_unit}
                    onChange={(e) => setFormData({ ...formData, cost_unit: e.target.value })}
                    placeholder="18.00"
                    required
                    data-testid="produit-cost-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Stock initial</Label>
                  <Input
                    type="number"
                    value={formData.stock_initial}
                    onChange={(e) => setFormData({ ...formData, stock_initial: e.target.value })}
                    placeholder="100"
                    data-testid="produit-stock-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prix B2C TTC (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.prix_b2c_ttc}
                    onChange={(e) => setFormData({ ...formData, prix_b2c_ttc: e.target.value })}
                    placeholder="35.00"
                    required
                    data-testid="produit-prix-b2c-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Prix B2B HT (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.prix_b2b_ht}
                    onChange={(e) => setFormData({ ...formData, prix_b2b_ht: e.target.value })}
                    placeholder="35.00"
                    required
                    data-testid="produit-prix-b2b-input"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="submit-produit-btn">
                Ajouter le produit
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="catalogue" className="space-y-4">
        <TabsList>
          <TabsTrigger value="catalogue" data-testid="tab-catalogue">
            <Boxes className="w-4 h-4 mr-2" />
            Catalogue
          </TabsTrigger>
          <TabsTrigger value="couts" data-testid="tab-couts">
            <Calculator className="w-4 h-4 mr-2" />
            Détail Coûts
          </TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services">
            <Sparkles className="w-4 h-4 mr-2" />
            Services
          </TabsTrigger>
        </TabsList>

        <TabsContent value="catalogue">
          <Card data-testid="catalogue-card">
            <CardHeader>
              <CardTitle className="text-lg">Catalogue Produits ({produits.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : produits.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Boxes className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun produit</p>
                  <p className="text-sm mt-1">Initialisez les données depuis le Dashboard ou ajoutez manuellement</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(productsByCategory).map(([categorie, prods]) => (
                    <div key={categorie}>
                      <h3 className="text-lg font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <span className="px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-700">
                          {categorie}
                        </span>
                        <span className="text-sm font-normal text-slate-500">({prods.length} produits)</span>
                      </h3>
                      <div className="overflow-x-auto border rounded-lg">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-16">Photo</TableHead>
                              <TableHead>SKU</TableHead>
                              <TableHead>Modèle</TableHead>
                              <TableHead>Couleur</TableHead>
                              <TableHead className="text-right">Coût (€)</TableHead>
                              <TableHead className="text-right">Prix B2C</TableHead>
                              <TableHead className="text-right">Prix B2B</TableHead>
                              <TableHead className="text-right">Stock</TableHead>
                              <TableHead>En situation (5 max)</TableHead>
                              <TableHead>Précommande</TableHead>
                              <TableHead className="text-center w-24">Précommande publique</TableHead>
                              <TableHead></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {prods.map((produit) => (
                              <TableRow key={produit.id} data-testid={`produit-row-${produit.id}`}>
                                <TableCell>
                                  <label className="relative block w-12 h-12 rounded-lg overflow-hidden border cursor-pointer bg-slate-50 hover:border-indigo-400" data-testid={`photo-cell-${produit.id}`}>
                                    {produit.image_url ? (
                                      <img src={produit.image_url} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-slate-300">
                                        <ImageIcon className="w-5 h-5" />
                                      </div>
                                    )}
                                    {uploadingId === produit.id && (
                                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                        <Loader2 className="w-4 h-4 text-white animate-spin" />
                                      </div>
                                    )}
                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadImage(produit, e.target.files?.[0])} data-testid={`photo-input-${produit.id}`} />
                                  </label>
                                </TableCell>
                                <TableCell className="font-mono text-sm">{produit.sku}</TableCell>
                                <TableCell className="font-medium">{produit.modele}</TableCell>
                                <TableCell>{produit.couleur}</TableCell>
                                <TableCell className="text-right">{produit.cost_unit.toFixed(2)}</TableCell>
                                <TableCell className="text-right">{produit.prix_b2c_ttc.toFixed(2)}</TableCell>
                                <TableCell className="text-right">{produit.prix_b2b_ht.toFixed(2)}</TableCell>
                                <TableCell className="text-right">{produit.stock_restant}</TableCell>
                                <TableCell>
                                  {/* Multi-images (5 max) */}
                                  <div className="flex items-center gap-1 flex-wrap" data-testid={`gallery-cell-${produit.id}`}>
                                    {(produit.images || []).map((url, i) => (
                                      <div key={i} className="relative w-8 h-8 rounded overflow-hidden border group">
                                        <img src={url} alt="" className="w-full h-full object-cover" />
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveAdditionalImage(produit, i)}
                                          className="absolute inset-0 bg-red-500/80 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-xs transition"
                                          data-testid={`remove-img-${produit.id}-${i}`}
                                          title="Supprimer"
                                        >×</button>
                                      </div>
                                    ))}
                                    {(produit.images?.length || 0) < 5 && (
                                      <label className="w-8 h-8 rounded border-2 border-dashed border-slate-300 hover:border-indigo-400 flex items-center justify-center cursor-pointer text-slate-400 text-lg" data-testid={`add-img-${produit.id}`}>
                                        +
                                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadAdditionalImage(produit, e.target.files?.[0])} />
                                      </label>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col items-start gap-1">
                                    <button
                                      type="button"
                                      onClick={() => handleTogglePrecommande(produit)}
                                      className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${produit.en_precommande ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}
                                      data-testid={`precommande-toggle-${produit.id}`}
                                    >
                                      {produit.en_precommande ? "Précommande ✓" : "Précommande OFF"}
                                    </button>
                                    {produit.en_precommande && (
                                      <input
                                        type="date"
                                        value={(produit.date_arrivee_estimee || "").slice(0, 10)}
                                        onChange={(e) => handleUpdateDateArrivee(produit, e.target.value)}
                                        className="text-[10px] border rounded px-1 py-0.5 w-28"
                                        data-testid={`precommande-date-${produit.id}`}
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleToggleVisible(produit)}
                                    className={produit.visible_precommande ? "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" : "text-slate-400 hover:text-slate-600"}
                                    title={produit.visible_precommande ? "Visible sur /precommande — cliquer pour masquer" : "Masqué — cliquer pour rendre visible sur /precommande"}
                                    data-testid={`toggle-visible-${produit.id}`}
                                  >
                                    {produit.visible_precommande ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                  </Button>
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(produit.id)}
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                    data-testid={`delete-produit-${produit.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="couts">
          <Card data-testid="couts-detail-card">
            <CardHeader>
              <CardTitle className="text-lg">Détail Complet des Coûts par Produit</CardTitle>
            </CardHeader>
            <CardContent>
              {coutsDetail.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun détail de coûts</p>
                  <p className="text-sm mt-1">Initialisez les données depuis le Dashboard</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Modèle</TableHead>
                        <TableHead className="text-right">Quantité</TableHead>
                        <TableHead className="text-right">Prix Usine</TableHead>
                        <TableHead className="text-right">Transport</TableHead>
                        <TableHead className="text-right">Taxes</TableHead>
                        <TableHead className="text-right">Import Dépôt</TableHead>
                        <TableHead className="text-right">Stockage Consommé</TableHead>
                        <TableHead className="text-right">Coût Réel Actuel</TableHead>
                        <TableHead className="text-right">Stockage Futur/Mois</TableHead>
                        <TableHead className="text-right">Coût Futur</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {coutsDetail.map((cout) => (
                        <TableRow key={cout.id} data-testid={`cout-row-${cout.id}`}>
                          <TableCell className="font-medium">{cout.modele}</TableCell>
                          <TableCell className="text-right">{cout.quantite}</TableCell>
                          <TableCell className="text-right">{cout.prix_usine.toFixed(2)} €</TableCell>
                          <TableCell className="text-right">{cout.transport.toFixed(2)} €</TableCell>
                          <TableCell className="text-right">{cout.taxes.toFixed(2)} €</TableCell>
                          <TableCell className="text-right">{cout.import_depot.toFixed(2)} €</TableCell>
                          <TableCell className="text-right">{cout.stockage_consomme.toFixed(2)} €</TableCell>
                          <TableCell className="text-right font-semibold text-indigo-600">
                            {cout.cout_reel_actuel.toFixed(2)} €
                          </TableCell>
                          <TableCell className="text-right">{cout.stockage_futur_mois.toFixed(2)} €</TableCell>
                          <TableCell className="text-right font-semibold text-amber-600">
                            {cout.cout_futur.toFixed(2)} €
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="services">
          <ServicesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Produits;
